import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import server from '../api/server'
import { getRealPrice, getTaxImport } from '@/utils/product'

export const useProductsStore = defineStore('productsStore', () => {
    const isLoading = ref(false)
    const isCategorySelected = ref(false)
    const products = ref([])
    const currentFilters = ref({}) // Guardar filtros actuales para paginación
    const pagination = ref({
        total: 0,
        total_pages: 0,
        current_page: 1,
        per_page: 20
    })
    
    const defaultParams = {
        per_page: 20,
        status: 'publish',
        orderby: 'date',
        page: 1
    }

    async function fetchProducts(params = {}, loadMore = false) {
        if(isLoading.value)
            return

        try {
            isLoading.value = true
            params = { ...defaultParams, ...params }
            isCategorySelected.value = params?.category

            // Guardar filtros actuales cuando NO es loadMore (nueva búsqueda)
            if (!loadMore) {
                const { page, ...filters } = params
                currentFilters.value = filters
            }

            const { data } = await server.get(`/stripe-payment-gateway/v1/products`, { params })

            const processedProducts = data.results.map(product => {
                const variations = product.variations?.map(variation => buildProductObject(variation, product)) || []

                return {
                    ...buildProductObject(product),
                    variations
                }
            })

            if (loadMore) {
                // Agregar productos a los existentes
                products.value = [...products.value, ...processedProducts]
            } else {
                // Reemplazar productos (primera carga o filtro nuevo)
                products.value = processedProducts
            }

            // Actualizar info de paginación
            if (data.pagination) {
                pagination.value = data.pagination
            }
            
            isLoading.value = false
            return true
        } catch(err) {
            console.log(err, 'error')
            isLoading.value = false
            return false
        }
    }

    async function loadMoreProducts(params = {}) {
        // Verificar si hay más productos
        if (pagination.value.current_page >= pagination.value.total_pages) {
            console.log('No hay más productos para cargar')
            return false
        }

        const nextPage = pagination.value.current_page + 1
        // Combinar filtros guardados con el número de página
        const mergedParams = { ...currentFilters.value, page: nextPage }
        return await fetchProducts(mergedParams, true)
    }

    function resetProducts() {
        products.value = []
        pagination.value = {
            total: 0,
            total_pages: 0,
            current_page: 1,
            per_page: 20
        }
    }

    function setProducts(args) {
        products.value = args
    }

    function buildProductObject(data, parent = null) {
        const {regular_price, sale_price} = getRealPrice(data)
        const price = !sale_price ? regular_price : sale_price
        const taxAmount = getTaxImport(parent ? parent : data, price)

        const prices = {
            tax_amount: taxAmount,
            regular_price,
            sale_price,
            price,
            total: (taxAmount + price)
        }

        return {
            ...data,
            prices: prices,
            images: [{ src: data.image || data.images[0].src, id: 'featured_image' }]
        }
    }

    const hasMoreProducts = computed(() => {
        return pagination.value.current_page < pagination.value.total_pages
    })

    return { 
        isLoading,
        products,
        pagination,
        isCategorySelected,
        hasMoreProducts,
        fetchProducts,
        loadMoreProducts,
        resetProducts,
        buildProductObject,
        setProducts,
    }
})
